import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "@/components/Header";
import KPIOverview from "@/components/dashboard/KPIOverview";
import ComercialKPIs from "@/components/dashboard/ComercialKPIs";
import XLSXUploader from "@/components/importer/XLSXUploader";
import TableToolbar from "@/components/events/TableToolbar";
import EventsTable from "@/components/events/EventsTable";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [estadoFilter, setEstadoFilter] = useState([]);
  const [sort, setSort] = useState({ key: "data_evento", dir: "desc" });
  const [totalEquipa, setTotalEquipa] = useState(0);
  const [kpiLabels, setKpiLabels] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => {
      setTotalEquipa(r.data.total_equipa);
      setKpiLabels(r.data.kpi_labels || {});
    }).catch(() => {});
  }, []);

  const onSaveTotalEquipa = async (v) => {
    try {
      const { data } = await api.put("/settings", { total_equipa: v });
      setTotalEquipa(data.total_equipa);
      toast.success("Total Equipa atualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (clientFilter) params.cliente = clientFilter;
      if (estadoFilter.length) params.estado = estadoFilter.join(",");
      const [evRes, stRes, clRes] = await Promise.all([
        api.get("/events", { params }),
        api.get("/events/stats", { params }),
        api.get("/events/clients"),
      ]);
      setEvents(evRes.data);
      setStats(stRes.data);
      setClients(clRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search, clientFilter, estadoFilter]);
  useEffect(() => {
    const t = setTimeout(fetchEvents, 300);
    return () => clearTimeout(t);
  }, [fetchEvents]);

  const onSave = async (id, patch) => {
    try {
      await api.put(`/events/${id}`, patch);
      toast.success("Alteração guardada");
      fetchEvents();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Evento eliminado");
      fetchEvents();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      if (search) params.set("search", search);
      if (clientFilter) params.set("cliente", clientFilter);
      if (estadoFilter.length) params.set("estado", estadoFilter.join(","));
      const res = await api.get(`/events/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "csv" ? "eventos.csv" : "eventos.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exportação concluída");
    } catch (err) {
      toast.error("Falha ao exportar");
    }
  };

  const sortedEvents = useMemo(() => {
    const arr = [...events];
    arr.sort((a, b) => {
      const va = a[sort.key] ?? "";
      const vb = b[sort.key] ?? "";
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), "pt", { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [events, sort]);

  const hasFilters = !!search || !!clientFilter || estadoFilter.length > 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Painel de Eventos</h1>
          <p className="text-sm text-muted-foreground mt-1">Consolide orçamentos, acompanhe valores, custos e margens.</p>
        </div>

        <KPIOverview stats={stats} totalEquipa={totalEquipa} onSaveTotalEquipa={onSaveTotalEquipa} labels={kpiLabels} />
        <ComercialKPIs data={stats?.by_comercial} />
        <XLSXUploader onSaved={fetchEvents} />

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold tracking-tight">Eventos ({events.length})</h2>
          </div>
          <TableToolbar
            search={search} setSearch={setSearch}
            clientFilter={clientFilter} setClientFilter={setClientFilter}
            estadoFilter={estadoFilter} setEstadoFilter={setEstadoFilter}
            clients={clients} onClear={() => { setSearch(""); setClientFilter(""); setEstadoFilter([]); }}
            onExport={onExport} hasFilters={hasFilters}
          />
          <EventsTable
            events={sortedEvents} loading={loading}
            onSave={onSave} onDelete={onDelete}
            sort={sort} setSort={setSort}
          />
        </div>
      </main>
    </div>
  );
}
